import _styled from "styled-components";
import { useMemo } from 'react';
import Image from 'next/image';
import { BuildingOfficeIcon, MapPinIcon, UsersIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { customImageLoader, domainInterestOptions } from '@restworld/utils-common';
import { Badge } from '@restworld/ui-ds-base';
import { Title } from '../title';
import { ImageSlider } from '../image-slider';
import Paragraph from '../paragraph';
import { V2Link } from '../../feature-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function EmployerCard(_ref) {
  var children = _ref.children,
      href = _ref.href,
      onClick = _ref.onClick,
      twStyles = _ref.twStyles;
  return /*#__PURE__*/_jsx(CardLinkContainer, {
    href: href,
    onClick: onClick,
    $twStyles: twStyles,
    prefetch: true,
    children: children
  });
}
export default EmployerCard;
export function EmployerCardHeader(_ref2) {
  var logo = _ref2.logo,
      title = _ref2.title,
      badge = _ref2.badge;
  return /*#__PURE__*/_jsxs(CardHeaderWrapper, {
    children: [/*#__PURE__*/_jsxs(LogoAndTitleWrapper, {
      children: [logo && /*#__PURE__*/_jsx(LogoWrapper, {
        children: /*#__PURE__*/_jsx(_StyledImage, {
          src: logo,
          alt: "",
          fill: true,
          loader: customImageLoader
        })
      }), /*#__PURE__*/_jsx(TitleWrapper, {
        children: /*#__PURE__*/_jsx(Title, {
          fontWeight: "medium",
          fontSize: "extraLarge",
          twStyle: {
            "maxWidth": "100%",
            "overflow": "hidden",
            "textOverflow": "ellipsis",
            "whiteSpace": "nowrap",
            "fontSize": "1.25rem",
            "lineHeight": "1.75rem"
          },
          children: title
        })
      })]
    }), badge && /*#__PURE__*/_jsx(BadgeWrapper, {
      children: /*#__PURE__*/_jsx(Badge, {
        variant: badge.variant,
        children: badge.text
      })
    })]
  });
}
export function EmployerCardImages(_ref3) {
  var images = _ref3.images;
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(ImageSlider, {
      images: images
    })
  });
}
export function EmployerCardInfo(_ref4) {
  var info = _ref4.info;

  var _useTranslation = useTranslation('common'),
      translateCommom = _useTranslation.t;

  var DomainInterestIcon = useMemo(function () {
    var _domainInterestOption;

    return (_domainInterestOption = domainInterestOptions.find(function (option) {
      return option.key === info.domainInterestKey;
    })) === null || _domainInterestOption === void 0 ? void 0 : _domainInterestOption.iconElement;
  }, [info.domainInterestKey]);
  return /*#__PURE__*/_jsx(CardInfoWrapper, {
    children: InfoItems.map(function (_ref5) {
      var key = _ref5.key,
          icon = _ref5.icon;
      var value = info[key];
      return value ? /*#__PURE__*/_jsx(InfoItem, {
        icon: key === 'occupation' && DomainInterestIcon ? /*#__PURE__*/_jsx(DomainInterestIcon, {
          className: "h-4 w-4"
        }) : icon,
        text: "".concat(value, " ").concat(key === 'openPositions' ? translateCommom('people_wanted') : '')
      }, key) : null;
    })
  });
}
export function EmployerCardFooter(_ref6) {
  var labels = _ref6.labels,
      notification = _ref6.notification;
  var hasLabels = !!(labels !== null && labels !== void 0 && labels.length);
  return /*#__PURE__*/_jsxs(CardFooterWrapper, {
    children: [!!(labels !== null && labels !== void 0 && labels.length) && /*#__PURE__*/_jsx(LabelsWrapper, {
      children: labels.map(function (_ref7) {
        var text = _ref7.text,
            _ref7$variant = _ref7.variant,
            variant = _ref7$variant === void 0 ? 'info' : _ref7$variant;
        return /*#__PURE__*/_jsx(Badge, {
          variant: variant,
          children: text
        }, text);
      })
    }), !!(notification !== null && notification !== void 0 && notification.candidateCount) && (notification === null || notification === void 0 ? void 0 : notification.message) && notification.candidates && /*#__PURE__*/_jsx(NotificationSectionWrapper, {
      hasLabels: hasLabels,
      children: /*#__PURE__*/_jsx(NewCandidateNotification, {
        candidates: notification.candidates,
        message: notification.message,
        candidateCount: notification.candidateCount,
        isAllSpontaneous: notification.isAllSpontaneous
      })
    })]
  });
}

function InfoItem(_ref8) {
  var icon = _ref8.icon,
      text = _ref8.text;
  return /*#__PURE__*/_jsxs(InfoItemWrapper, {
    children: [/*#__PURE__*/_jsx(IconWrapper, {
      children: icon
    }), /*#__PURE__*/_jsx(Paragraph, {
      font: "medium",
      standardVariant: "body-sm",
      color: "gray-500",
      children: text
    })]
  });
}

export function NewCandidateNotification(_ref9) {
  var isAllSpontaneous = _ref9.isAllSpontaneous,
      message = _ref9.message,
      wrapperStyle = _ref9.wrapperStyle,
      imageWrapperStyle = _ref9.imageWrapperStyle,
      _ref9$candidateCount = _ref9.candidateCount,
      candidateCount = _ref9$candidateCount === void 0 ? 0 : _ref9$candidateCount,
      _ref9$candidates = _ref9.candidates,
      candidates = _ref9$candidates === void 0 ? [] : _ref9$candidates,
      _ref9$imageDisplayLim = _ref9.imageDisplayLimit,
      imageDisplayLimit = _ref9$imageDisplayLim === void 0 ? 3 : _ref9$imageDisplayLim,
      _ref9$leftOffset = _ref9.leftOffset,
      leftOffset = _ref9$leftOffset === void 0 ? 16 : _ref9$leftOffset;
  var moreToShowCount = candidateCount && candidateCount - imageDisplayLimit;
  var hasMoreToShow = !!moreToShowCount && moreToShowCount > 0;
  var textLeftOffset = (Math.min(candidateCount, imageDisplayLimit) - (candidateCount === 2 || candidateCount === 3 ? 1 : 0)) * leftOffset - 0;
  return candidates !== null && candidates !== void 0 && candidates.length ? /*#__PURE__*/_jsxs(NotificationWrapper, {
    isSpontaneous: isAllSpontaneous,
    twStyle: wrapperStyle,
    children: [/*#__PURE__*/_jsxs(_StyledDiv, {
      children: [candidates.slice(0, imageDisplayLimit).map(function (candidate, i) {
        return /*#__PURE__*/_jsx(RoundedWrapper, {
          isPositioned: i > 0,
          twStyles: [{
            left: i === 1 ? 18 : i * leftOffset
          }, imageWrapperStyle || {}],
          children: candidate.image ? /*#__PURE__*/_jsx(_StyledImage2, {
            src: candidate.image,
            alt: "",
            width: 28,
            height: 28,
            loader: customImageLoader
          }) : /*#__PURE__*/_jsx(Title, {
            fontSize: "base",
            fontWeight: "semibold",
            twStyle: [{
              "display": "grid",
              "height": "1.75rem",
              "width": "1.75rem",
              "placeItems": "center",
              "borderRadius": "9999px",
              "--tw-bg-opacity": "1",
              "backgroundColor": "rgb(240 235 248 / var(--tw-bg-opacity))",
              "fontSize": "0.875rem",
              "lineHeight": "1.25rem",
              "textTransform": "uppercase",
              "--tw-text-opacity": "1",
              "color": "rgb(72 36 133 / var(--tw-text-opacity))"
            }],
            children: "".concat(candidate !== null && candidate !== void 0 && candidate.firstName ? candidate.firstName[0] : '').concat(candidate !== null && candidate !== void 0 && candidate.lastName ? candidate.lastName[0] : '')
          })
        }, i);
      }), !!hasMoreToShow && /*#__PURE__*/_jsx(RoundedWrapper, {
        isPositioned: true,
        twStyles: [{
          left: imageDisplayLimit * leftOffset
        }, imageWrapperStyle || {}],
        children: /*#__PURE__*/_jsx(Title, {
          fontSize: "base",
          fontWeight: "semibold",
          twStyle: [{
            "display": "grid",
            "height": "1.75rem",
            "width": "1.75rem",
            "placeItems": "center",
            "borderRadius": "9999px",
            "--tw-bg-opacity": "1",
            "backgroundColor": "rgb(246 245 255 / var(--tw-bg-opacity))",
            "fontSize": "0.875rem",
            "lineHeight": "1.25rem",
            "--tw-text-opacity": "1",
            "color": "rgb(105 62 181 / var(--tw-text-opacity))"
          }],
          children: "+".concat(moreToShowCount)
        })
      })]
    }), !!message && /*#__PURE__*/_jsx(Paragraph, {
      font: "medium",
      standardVariant: "body-sm",
      color: "white",
      twStyle: {
        marginLeft: candidateCount > 1 ? textLeftOffset : 0
      },
      children: message
    })]
  }) : null;
}
var InfoIconMap = {
  address: /*#__PURE__*/_jsx(MapPinIcon, {
    className: "h-4 w-4"
  }),
  businessName: /*#__PURE__*/_jsx(BuildingOfficeIcon, {
    className: "h-4 w-4"
  }),
  occupation: /*#__PURE__*/_jsx(BuildingOfficeIcon, {
    className: "h-4 w-4"
  }),
  openPositions: /*#__PURE__*/_jsx(UsersIcon, {
    className: "h-4 w-4"
  })
};
var InfoItems = [{
  key: 'businessName',
  icon: InfoIconMap.businessName
}, {
  key: 'occupation',
  icon: InfoIconMap.occupation
}, {
  key: 'address',
  icon: InfoIconMap.address
}, {
  key: 'openPositions',
  icon: InfoIconMap.openPositions
}];

var CardLinkContainer = /*#__PURE__*/_styled(V2Link).withConfig({
  displayName: "employer-card__CardLinkContainer",
  componentId: "sc-hzsrl2-0"
})(["", " ", ""], {
  "boxSizing": "border-box",
  "display": "flex",
  "flexDirection": "column",
  "gap": "1rem",
  "borderRadius": "1rem",
  "borderWidth": "1px",
  "--tw-border-opacity": "1",
  "borderColor": "rgb(222 222 225 / var(--tw-border-opacity))",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(250 250 250 / var(--tw-bg-opacity))",
  "padding": "1.25rem",
  "transitionProperty": "all",
  "transitionTimingFunction": "cubic-bezier(0.4, 0, 0.2, 1)",
  "transitionDuration": "150ms",
  ":hover": {
    "--tw-border-opacity": "1",
    "borderColor": "rgb(102 51 187 / var(--tw-border-opacity))",
    "--tw-shadow": "inset 0 0 0 1px #6633bb",
    "--tw-shadow-colored": "inset 0 0 0 1px var(--tw-shadow-color)",
    "boxShadow": "var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow)"
  }
}, function (_ref10) {
  var $twStyles = _ref10.$twStyles;
  return $twStyles || [];
});

var CardHeaderWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__CardHeaderWrapper",
  componentId: "sc-hzsrl2-1"
})(["", ""], {
  "display": "flex",
  "maxWidth": "100%",
  "flexDirection": "column",
  "gap": "0.5rem",
  "@media (min-width: 768px)": {
    "flexDirection": "row",
    "alignItems": "center",
    "justifyContent": "space-between"
  }
});

var LogoWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__LogoWrapper",
  componentId: "sc-hzsrl2-2"
})(["", ""], {
  "position": "relative",
  "display": "flex",
  "height": "2rem",
  "width": "2rem",
  "minWidth": "2rem",
  "alignItems": "center",
  "justifyContent": "center",
  "overflow": "hidden",
  "borderRadius": "0.5rem",
  "borderWidth": "1px",
  "--tw-border-opacity": "1",
  "borderColor": "rgb(222 222 225 / var(--tw-border-opacity))",
  "objectFit": "cover",
  "objectPosition": "center"
});

var LogoAndTitleWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__LogoAndTitleWrapper",
  componentId: "sc-hzsrl2-3"
})(["", ""], {
  "display": "flex",
  "width": "100%",
  "alignItems": "center",
  "gap": "0.5rem",
  "@media (min-width: 640px)": {
    "width": "100%"
  },
  "@media (min-width: 768px)": {
    "width": "75%",
    "maxWidth": "75%"
  },
  "@media (min-width: 1536px)": {
    "maxWidth": "62%"
  }
});

var TitleWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__TitleWrapper",
  componentId: "sc-hzsrl2-4"
})(["", ""], {
  "maxWidth": "100%",
  "overflow": "hidden",
  "textOverflow": "ellipsis",
  "whiteSpace": "nowrap"
});

var BadgeWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__BadgeWrapper",
  componentId: "sc-hzsrl2-5"
})(["", ""], {
  "minWidth": "fit-content",
  "maxWidth": "fit-content",
  "whiteSpace": "nowrap",
  "textTransform": "lowercase"
});

var CardInfoWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__CardInfoWrapper",
  componentId: "sc-hzsrl2-6"
})(["", ""], {
  "display": "flex",
  "flexWrap": "wrap",
  "gap": "0.5rem"
});

var CardFooterWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__CardFooterWrapper",
  componentId: "sc-hzsrl2-7"
})(["", ""], {
  "display": "flex",
  "flexDirection": "column",
  "gap": "1rem",
  "@media (min-width: 768px)": {
    "flexDirection": "row",
    "alignItems": "center"
  }
});

var NotificationSectionWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__NotificationSectionWrapper",
  componentId: "sc-hzsrl2-8"
})(["", " ", ""], {
  "display": "flex",
  "flex": "1 1 0%",
  "borderRadius": "9999px"
}, function (_ref11) {
  var hasLabels = _ref11.hasLabels;
  return hasLabels ? {
    "@media (min-width: 768px)": {
      "justifyContent": "flex-end"
    }
  } : {
    "@media (min-width: 768px)": {
      "justifyContent": "flex-start"
    }
  };
});

var LabelsWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__LabelsWrapper",
  componentId: "sc-hzsrl2-9"
})(["", ""], {
  "display": "flex",
  "flex": "1 1 0%",
  "flexWrap": "wrap",
  "gap": "0.5rem",
  "textTransform": "lowercase"
});

var InfoItemWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__InfoItemWrapper",
  componentId: "sc-hzsrl2-10"
})(["", ""], {
  "display": "flex",
  "width": "fit-content",
  "flexWrap": "nowrap",
  "alignItems": "center",
  "gap": "0.25rem"
});

var IconWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__IconWrapper",
  componentId: "sc-hzsrl2-11"
})(["", ""], {
  "height": "1rem",
  "width": "1rem"
});

var RoundedWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__RoundedWrapper",
  componentId: "sc-hzsrl2-12"
})(function (_ref12) {
  var twStyles = _ref12.twStyles;
  return [{
    "display": "grid",
    "height": "2rem",
    "width": "2rem",
    "placeItems": "center",
    "borderRadius": "9999px",
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))"
  }, function (_ref13) {
    var isPositioned = _ref13.isPositioned;
    return isPositioned && {
      "position": "absolute"
    };
  }, twStyles || {}];
});

var NotificationWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-card__NotificationWrapper",
  componentId: "sc-hzsrl2-13"
})(function (_ref14) {
  var isSpontaneous = _ref14.isSpontaneous,
      twStyle = _ref14.twStyle;
  return [{
    "position": "relative",
    "display": "flex",
    "minHeight": "40px",
    "width": "fit-content",
    "alignItems": "center",
    "gap": "0.5rem",
    "overflow": "hidden",
    "textOverflow": "ellipsis",
    "whiteSpace": "nowrap",
    "borderRadius": "9999px",
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(102 51 187 / var(--tw-bg-opacity))",
    "padding": "0.25rem",
    "paddingRight": "0.75rem"
  }, twStyle || {}];
});

var _StyledImage = /*#__PURE__*/_styled(Image).withConfig({
  displayName: "employer-card___StyledImage",
  componentId: "sc-hzsrl2-14"
})({
  "objectFit": "cover",
  "objectPosition": "center"
});

var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  displayName: "employer-card___StyledDiv",
  componentId: "sc-hzsrl2-15"
})({
  "display": "flex"
});

var _StyledImage2 = /*#__PURE__*/_styled(Image).withConfig({
  displayName: "employer-card___StyledImage2",
  componentId: "sc-hzsrl2-16"
})({
  "height": "1.75rem",
  "width": "1.75rem",
  "borderRadius": "9999px",
  "objectFit": "cover",
  "objectPosition": "center"
});